/**
 *  Games launcher
 */

define(function(require, exports, module) {
    'use strict';
    var core = require('core');
    var config = require('config');
    var url = require('util/url.util');
    var i18n = require('i18n');
    var device = require('util/device.util');
    var user = require('user');
    var GamesinfoGameModel = require('portlets/games-info/gamesinfo.game.model');
    const { dispatcher } = require('bundle');
    const historyUtil = require('util/history.util');
    const server = require('server');
    const serverUtil = require('util/server.util');

    // Game Platform
    var Base3rdPartyPlatform = require('modules/launcher/platforms/base.3rdParty.platform');

    module.exports = {

        /**
         * Game that launched right now
         * @type {Object}
         */
        launchedGame: null,

        originalGameMode: null,

        /**
         * Launch game
         * @param  {String} gameCode Model of Game
         * @param  {Number} mode "Real" or "Demo" mode for game
         * @param  {String=} queryString - additional URL params
         */
        launchGame: function(gameCode, mode, queryString, options) {
            const { ageVerificationStatus } = user.get('userDetails');
            const isDataVerificationStatusNeeded = config.get('enableDataVerificationStatusCheck')
                && ['failed', 'review', 'unknown'].includes(ageVerificationStatus);

            // disable Game launch for Admin
            if (user.isAdmin()) {
                window.Playtech.API.popup.showPopup({
                    title: i18n.t('playtech.mobile.system.launcher.gameLaunchPopup.title'),
                    content: i18n.t('playtech.mobile.system.launcher.gameLaunchPopup.content')
                });

                return;
            }

            if (isDataVerificationStatusNeeded) {
                console.warn('Game playing is disabled because of "DataVerificationStatus" verification is needed');

                // in case loginPage is configured
                this.navigateFromGamingPath();

                return;
            }

            // continue register for fast registered players
            if (user.isFastRegistered()) {
                window.Playtech.API.popup.closePopups();

                return user.continueRegistration(true,
                    {
                        code: gameCode,
                        mode: mode,
                        queryString: queryString,
                        options: options
                    });
            }

            this.removeCommand(); // remove saved command id from previous game session

            const game = this._getGame(gameCode);

            if (!game) {
                this.navigateFromGamingPath();

                return;
            }

            // Real mode by default
            mode = mode !== undefined ? mode : 1;

            // If we try to open demo mode need to check if it's available
            if (!mode || this.originalGameMode === 0) {
                this.originalGameMode = mode;

                var gameModesMap = {
                    guest: Number(user.isLoggedIn()),
                    user: Number(!user.isLoggedIn()),
                    both: 0,
                    unavailable: 1
                };

                mode = gameModesMap[game.funSupport];
            }

            var isNeedToRefreshPage = this._isLaunchInNewWindow(gameCode);

            if (mode && !user.isLoggedIn()) {
                // Open login popup for guest
                core.trigger('app:message', {
                    ID: 'showLogin',
                    data: {
                        afterLoginUrl: '/igaming/' + gameCode,
                        data: {
                            isRefreshPage: isNeedToRefreshPage
                        },
                        onLogin: () => this._handleSuccessLogin(gameCode, mode, queryString, options),
                        closeAsync: true
                    }
                });

                return;
            }

            window.Playtech.API.popup.closePopups('gameLaunch');
            dispatcher.dispatch('game:info:close', gameCode);

            this.originalGameMode = null;

            return this.launchWithLimitsVerification(gameCode, mode, queryString, options);
        },

        navigateFromGamingPath: function() {
            const { prevPath } = historyUtil.state?.state || {};

            if (url.isGamimgPath()) {
                core.navigate(prevPath || config.get('rootPage'));
            }
        },

        launchWithLimitsVerification: async function(gameCode, mode, queryString, options) {
            const launchMethod = this._getLaucherMethod(gameCode);
            const isVerificationNeeded = mode === 1
                && config.get('enableGamblingSessionLimitPopup')
                && ['redirectInNewWindow', 'redirectInThisWindow'].includes(launchMethod);

            if (isVerificationNeeded) {
                const showGamblingSessionLimitsPopup = () => window.Playtech.API.popup.showPopup('gamblingSessionLimits', {
                    onSuccessLimitsSet: () => this._launch(gameCode, mode, queryString, options)
                });

                try {
                    const { data } = await server.send({
                        ID: 35899,
                        responses: [35900, 35901],
                        errorLevel: 1,
                        description: 'Get gambling session limits'
                    });

                    const action = data?.actions?.find(item => item.actionShowSimpleDialogInfo)?.actionShowSimpleDialogInfo;

                    if (action) {
                        window.Playtech.API.popup.showPopup('simpleDialogInfo', {
                            message: action.message,
                            actionId: action.actionId,
                            onAccept: showGamblingSessionLimitsPopup,
                            acceptBtnText: i18n.t('playtech.mobile.system.gamblingSessionLimits.intervalPopup.submit'),
                            declineBtnText: i18n.t('playtech.mobile.system.gamblingSessionLimits.intervalPopup.cancel')
                        });

                        return;
                    }

                    if (!data?.gamblingSessionId) {
                        showGamblingSessionLimitsPopup();

                        return;
                    }
                } catch (error) {
                    window.Playtech.API.popup.showPopup({
                        title: 'playtech.mobile.system.gamblingSessionLimits.errorPopup.title',
                        content: serverUtil.getErrorMessage(error, { defaultMessage: error?.response?.data?.errorMessage })
                    });

                    return;
                }
            }

            return this._launch(gameCode, mode, queryString, options);
        },

        /**
         * Close game
         * @param  {Boolean}  hide   Hide game if true and close in other case
         */
        closeGame: function(hide) {
            var force = !hide;

            if (this.launchedGame) {
                this.launchedGame.close(force); // Remove iframe from DOM if false

                if (force) {
                    this.launchedGame = null;
                }
            }
        },

        /**
         * Detect type of 'closeGame' method after response page - successes
         */
        handlePageResponse: function() {
            if (this.launchedGame) {
                this.closeGame(this.onlyHideGame());
            }
        },

        /**
         * Save last called command
         */
        setCommand: function(command) {
            this.lastCalledCommand = command;
        },

        removeCommand: function() {
            delete this.lastCalledCommand;
        },

        onlyHideGame: function() {
            var command = this.lastCalledCommand || {};
            var type = command.pageType;

            return type && type !== 'lobby' ? true : false;
        },

        /**
         * Get launched method of game
         * @param  {String}  gameCode
         * @return {String}
         */
        _getLaucherMethod: function(gameCode) {
            var game = config.get('gamesConfiguration')[gameCode];
            var gameType = device.isMobilePlatform ? game.mobileGameType : game.gameType;
            var launcherOptions = Base3rdPartyPlatform.getLaunchOptions(gameType);

            if (!launcherOptions) {
                return core.error(`Launcher options for: ${gameType} game type are not configured in admin area`);
            }

            return device.isMobilePlatform ? launcherOptions.launchMethodMobile : launcherOptions.launchMethodDesktop;
        },

        _isLaunchInNewWindow: function(gameCode) {
            var launchedMethod = this._getLaucherMethod(gameCode);

            return launchedMethod === 'redirectInNewWindow';

        },

        /**
         * Get game from global configuration by gameCode
         * @param  {String} gameCode
         * @return {Object} object with game info
         */
        _getGame: function(gameCode) {
            const isLaunchedOnHosting = config.get('hosting');

            if (isLaunchedOnHosting) {
                // add game dynamically
                Playtech.Variables.gamesConfiguration[gameCode] = {
                    active: true,
                    formFactors: ['tablet', 'mobile'],
                    gameCode: gameCode,
                    mobile: true,
                    mobileGameCode: gameCode,
                    mobileGameType: url.getLocationParameterByName('gamePlatform') || 'NGM'
                };
            }

            const game = config.get('gamesConfiguration')[gameCode];

            if (!game) {
                core.error(`Game with game code: ${gameCode} is not configured`);

                return;
            }

            if (device.isMobilePlatform) {
                game.mobileGameCode = game.mobileGameCode || game.gameCode;
            }

            return game;
        },

        /**
         * Get url with params for game launch
         * @private
         * @param  {String} gameCode -- game code in gamesConfiguration
         * @param  {Number} mode     -- real or demo mode
         * @param  {String=} queryString -- additional URL params
         */
        _launch: function(gameCode, mode, queryString, options) {
            var game = this._getGame(gameCode);

            if (!game) {
                return;
            }

            user.set({
                launchedGame: {
                    queryString: queryString,
                    code: gameCode,
                    mode: mode,
                    options: options
                }
            });

            Base3rdPartyPlatform.setLauncher(this);
            Base3rdPartyPlatform.setAdditionalQueryString(queryString);
            Base3rdPartyPlatform.open(new GamesinfoGameModel(game), mode);

            if (options && options.isReactGame && options.onGameLaunched) {
                options.onGameLaunched();
            }

            core.trigger('game:launch', game, mode, queryString, options);
        },

        _handleSuccessLogin: function(gameCode, mode, queryString, options) {

            if (device.isMobilePlatform) {
                return this.launchGame(gameCode, mode, queryString, options);
            }

            if (this._isLaunchInNewWindow(gameCode)) {
                var game = this._getGame(gameCode);
                var buttonTitle = i18n.t('playtech.mobile.system.button.realPlay') + ' ' + game.name;
                var popupContent = i18n.t('playtech.mobile.system.games.popup-blocker-with-confirm');

                window.Playtech.API.popup.showPopup({
                    content: popupContent,
                    buttons: [
                        {
                            text: buttonTitle,
                            onClick: () => this.launchGame(gameCode, mode, queryString, options)
                        }
                    ],
                    onClose: function() {
                        core.trigger('page:refresh');
                    }
                });
            } else {
                this.launchGame(gameCode, mode, queryString, options);
            }
        }
    };
});
